/**
 * Egyptian Governorates (المحافظات) with Shipping Pricing
 * Data-driven shipping system for ZOE e-commerce platform
 */

export interface ShippingCity {
  id: number;
  code: string;
  nameEN: string;
  nameAR: string;
  shippingFee: number; // In EGP
}

export const SHIPPING_CITIES: ShippingCity[] = [
  // Greater Cairo Area - Lower Rates
  { id: 1, code: "cairo", nameEN: "Cairo", nameAR: "القاهرة", shippingFee: 80 },
  { id: 2, code: "giza", nameEN: "Giza", nameAR: "الجيزة", shippingFee: 80 },
  { id: 3, code: "qalyubia", nameEN: "Qalyubia", nameAR: "القليوبية", shippingFee: 90 },
  { id: 26, code: "cairo_outskirts", nameEN: "Cairo & Giza Outskirts", nameAR: "أطراف القاهرة والجيزة", shippingFee: 100 },

  // Alexandria - Major Port City
  { id: 4, code: "alexandria", nameEN: "Alexandria", nameAR: "الإسكندرية", shippingFee: 90 },

  // Delta Governorates - Medium Rates
  { id: 5, code: "sharqia", nameEN: "Sharqia", nameAR: "الشرقية", shippingFee: 100 },
  { id: 6, code: "dakahlia", nameEN: "Dakahlia", nameAR: "الدقهلية", shippingFee: 100 },
  { id: 7, code: "beheira", nameEN: "Beheira", nameAR: "البحيرة", shippingFee: 105 },
  { id: 8, code: "gharbia", nameEN: "Gharbia", nameAR: "الغربية", shippingFee: 100 },
  { id: 9, code: "monufia", nameEN: "Monufia", nameAR: "المنوفية", shippingFee: 95 },
  { id: 10, code: "kafr_el_sheikh", nameEN: "Kafr El Sheikh", nameAR: "كفر الشيخ", shippingFee: 105 },
  { id: 11, code: "damietta", nameEN: "Damietta", nameAR: "دمياط", shippingFee: 110 },

  // Canal Zone - Strategic Locations
  { id: 12, code: "ismailia", nameEN: "Ismailia", nameAR: "الإسماعيلية", shippingFee: 105 },
  { id: 13, code: "port_said", nameEN: "Port Said", nameAR: "بورسعيد", shippingFee: 110 },
  { id: 14, code: "suez", nameEN: "Suez", nameAR: "السويس", shippingFee: 110 },

  // Upper Egypt - Higher Rates Due to Distance
  { id: 15, code: "beni_suef", nameEN: "Beni Suef", nameAR: "بني سويف", shippingFee: 120 },
  { id: 16, code: "fayoum", nameEN: "Fayoum", nameAR: "الفيوم", shippingFee: 120 },
  { id: 17, code: "minya", nameEN: "Minya", nameAR: "المنيا", shippingFee: 130 },
  { id: 18, code: "asyut", nameEN: "Asyut", nameAR: "أسيوط", shippingFee: 140 },
  { id: 19, code: "sohag", nameEN: "Sohag", nameAR: "سوهاج", shippingFee: 150 },
  { id: 20, code: "qena", nameEN: "Qena", nameAR: "قنا", shippingFee: 160 },
  { id: 21, code: "luxor", nameEN: "Luxor", nameAR: "الأقصر", shippingFee: 170 },
  { id: 22, code: "aswan", nameEN: "Aswan", nameAR: "أسوان", shippingFee: 180 },

  // Frontier Governorates - Premium Rates
  { id: 23, code: "matrouh", nameEN: "Matrouh", nameAR: "مطروح", shippingFee: 200 },
  { id: 24, code: "north_sinai", nameEN: "North Sinai", nameAR: "شمال سيناء", shippingFee: 220 },
  { id: 25, code: "south_sinai", nameEN: "South Sinai", nameAR: "جنوب سيناء", shippingFee: 220 },
  { id: 27, code: "red_sea", nameEN: "Red Sea", nameAR: "البحر الأحمر", shippingFee: 200 },
  { id: 28, code: "new_valley", nameEN: "New Valley", nameAR: "الوادي الجديد", shippingFee: 250 }
];

/**
 * Get shipping city by code
 */
export const getShippingCityByCode = (code: string): ShippingCity | undefined => {
  return SHIPPING_CITIES.find(city => city.code === code);
};

/**
 * Get shipping city by ID
 */
export const getShippingCityById = (id: number): ShippingCity | undefined => {
  return SHIPPING_CITIES.find(city => city.id === id);
};

/**
 * Get localized city name
 */
export const getLocalizedCityName = (city: ShippingCity, language: 'en' | 'ar'): string => {
  return language === 'ar' ? city.nameAR : city.nameEN;
};

/**
 * Calculate shipping fee based on city
 */
export const calculateShippingFee = (cityId: number | null): number => {
  if (!cityId) return 0;
  const city = getShippingCityById(cityId);
  return city ? city.shippingFee : 0;
};

/**
 * Get delivery time estimate (3-7 days for most cities)
 */
export const getDeliveryTimeEstimate = (cityId: number | null): string => {
  if (!cityId) return "";
  
  const city = getShippingCityById(cityId);
  if (!city) return "";
  
  // Cairo & Giza: Fastest delivery
  if ([1, 2].includes(city.id)) {
    return "3–5 Days";
  }
  
  // Delta & nearby: Standard delivery
  if ([3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 26].includes(city.id)) {
    return "3–7 Days";
  }
  
  // Upper Egypt & Frontier: Extended delivery
  return "5–10 Days";
};